/**
 * Autonomy where it belongs (session drawer, project menu, machine drawer): what decides a
 * session's approvals, where each value comes from, and the presets the in-place controls
 * offer. Pure, so it is unit tested without React. The org-wide editors stay in Settings.
 */
import { durationText, expiryPolicyFor, sessionOrigin } from '@orgus/protocol';
import type { ApprovalExpiryPolicy, ApprovalExpirySettings, ControlMachineState, EffectiveSetting, Id, PolicyRule, Session, SessionAllow, SessionClaim } from '@orgus/protocol';
import { PERMISSION_MODES } from '@orgus/policy';
import { permissionLabel } from '../sessions/labels.ts';

// ---------------------------------------------------------------------------
// Who decides a session's approvals
// ---------------------------------------------------------------------------

/**
 * launched: Orgus started it, so its launch hooks ask Orgus (rules, then a person).
 * controlled: session control is on for its machine; Orgus answers explicit rules and
 * "Allow for session", the rest goes back to the session after the machine's wait.
 * watched: Orgus only reads it; Claude Code asks in the session.
 */
export type ApprovalRoute = 'launched' | 'controlled' | 'watched';

export function approvalRoute(s: Pick<Session, 'origin'>, control: Pick<ControlMachineState, 'installed'> | undefined): ApprovalRoute {
  if (sessionOrigin(s) === 'launched') return 'launched';
  return control?.installed ? 'controlled' : 'watched';
}

export function routeFact(route: ApprovalRoute): { value: string; note: string } {
  if (route === 'launched') return { value: 'Orgus decides', note: 'Rules, then a person' };
  if (route === 'controlled') return { value: 'Orgus first', note: 'Rules, then the session asks' };
  return { value: 'In the session', note: 'Orgus only watches' };
}

/** What happens to a request nobody answers, for a controlled session: back to the session after the machine's wait. */
export function controlWaitText(waitSeconds: number): string {
  return waitSeconds % 60 === 0 ? `Back to the session after ${durationText(waitSeconds / 60)}` : `Back to the session after ${waitSeconds} seconds`;
}

// ---------------------------------------------------------------------------
// Permission mode and where it comes from
// ---------------------------------------------------------------------------

/** Where a project's permission mode for new sessions comes from, in words. */
export function sourceText(source: EffectiveSetting['source'] | undefined, projectName: string | null): string {
  if (source === 'project') return projectName ? `Project ${projectName}` : 'This project';
  if (source === 'org') return 'Every project';
  if (source === 'session') return 'Picked at launch';
  return 'Built in';
}

/**
 * The session's permission mode and where it came from. A launched session got its mode from
 * the layers (or the launcher's pick, when it differs); a watched one from its own settings.
 */
export function permissionFact(
  s: Pick<Session, 'origin' | 'permissionMode'>,
  resolved: Pick<EffectiveSetting, 'value' | 'source'> | undefined,
  projectName: string | null,
): { value: string; note: string } {
  const value = permissionLabel(s.permissionMode);
  if (sessionOrigin(s) !== 'launched') return { value, note: 'Its own Claude Code settings' };
  const layer = typeof resolved?.value === 'string' ? resolved.value : null;
  const mine = s.permissionMode ?? 'default';
  if (layer && layer !== mine) return { value, note: 'Picked at launch' };
  if (!layer && mine !== 'default') return { value, note: 'Picked at launch' };
  return { value, note: sourceText(resolved?.source, projectName) };
}

/** The permission modes a project can set, with the session drawer's labels. */
export const MODE_OPTIONS: { value: string; label: string }[] = PERMISSION_MODES.map((m) => ({ value: m, label: permissionLabel(m) }));

// ---------------------------------------------------------------------------
// When nobody answers
// ---------------------------------------------------------------------------

/** One short line: "Deny after 15 minutes", "Escalate at 15 minutes, deny at 2 hours". */
export function expiryShort(p: ApprovalExpiryPolicy): string {
  if (p.onExpiry === 'hold') return `Escalate at ${durationText(p.timeoutMinutes)}, deny at ${durationText(p.holdMaxMinutes)}`;
  return `Deny after ${durationText(p.timeoutMinutes)}`;
}

export function expiryFact(settings: ApprovalExpirySettings | null | undefined, projectId: Id | null, projectName: string | null): { value: string; note: string; policy: ApprovalExpiryPolicy; own: boolean } {
  const policy = expiryPolicyFor(settings, projectId);
  const own = !!(projectId && settings?.projects[projectId] && Object.keys(settings.projects[projectId]!).length);
  return { value: expiryShort(policy), note: own ? sourceText('project', projectName) : 'Every project', policy, own };
}

/** Choices for a project's expiry: deny after a wait, or keep waiting up to 2 hours. Built on the org default so the reminder stays valid. */
export function expiryPresets(org: ApprovalExpiryPolicy): { key: string; label: string; policy: ApprovalExpiryPolicy }[] {
  const deny = (minutes: number): ApprovalExpiryPolicy => ({
    timeoutMinutes: minutes,
    onExpiry: 'deny',
    holdMaxMinutes: Math.max(org.holdMaxMinutes, minutes),
    remindBeforeMinutes: org.remindBeforeMinutes < minutes ? org.remindBeforeMinutes : 0,
  });
  const hold: ApprovalExpiryPolicy = { timeoutMinutes: org.timeoutMinutes, onExpiry: 'hold', holdMaxMinutes: Math.max(120, org.timeoutMinutes), remindBeforeMinutes: org.remindBeforeMinutes < org.timeoutMinutes ? org.remindBeforeMinutes : 0 };
  return [...[5, 15, 30, 60].map((m) => deny(m)), hold].map((policy) => ({ key: expiryKey(policy), label: expiryShort(policy), policy }));
}

/** A stable key for a policy, for a Select value. */
export function expiryKey(p: ApprovalExpiryPolicy): string {
  return `${p.onExpiry}:${p.timeoutMinutes}:${p.holdMaxMinutes}:${p.remindBeforeMinutes}`;
}

// ---------------------------------------------------------------------------
// Rules, grants, claims
// ---------------------------------------------------------------------------

/** A project's own rules, deny first, then ask, then allow; off rules last. */
export function projectRules(rules: readonly PolicyRule[], projectId: Id): PolicyRule[] {
  const rank = { deny: 0, ask: 1, allow: 2 } as const;
  return rules
    .filter((r) => r.scope.kind === 'project' && r.scope.projectId === projectId)
    .sort((a, b) => Number(a.enabled === false) - Number(b.enabled === false) || rank[a.decision] - rank[b.decision] || a.createdAt - b.createdAt);
}

export function orgRuleCount(rules: readonly PolicyRule[]): number {
  return rules.filter((r) => r.scope.kind === 'org' && r.enabled !== false).length;
}

/** This session's "Allow for session" answers, newest first. */
export function sessionAllowsFor(allows: readonly SessionAllow[] | undefined, s: Pick<Session, 'id' | 'machineId'>): SessionAllow[] {
  return (allows ?? []).filter((a) => a.sessionId === s.id && a.machineId === s.machineId).sort((a, b) => b.at - a.at);
}

/** "Bash npm install:*", or "Bash, any use" when the allow has no pattern. */
export function allowText(a: Pick<SessionAllow, 'tool' | 'pattern'>): string {
  return a.pattern ? `${a.tool} ${a.pattern}` : `${a.tool}, any use`;
}

/**
 * The claim action the drawer's footer offers. Claim makes the session yours (the teams owner:
 * its requests come to you, and a discovered session is no longer read-only); without the teams
 * module it is the session-control claim, which only lifts read-only. Release takes back a
 * session-control claim (a teams owner has no release yet).
 */
export type ClaimAction = { kind: 'claim'; via: 'teams' | 'session'; blocked: string | null } | { kind: 'release'; blocked: string | null } | { kind: 'none' };

const OPERATOR = ['owner', 'admin', 'operator'];

export function claimAction(o: {
  origin: 'launched' | 'discovered';
  /** The session-control claim (POST /api/sessions/:id/claim), not the teams owner. */
  claim: SessionClaim | null;
  /** null: the teams module is not here; else the owner, or '' for nobody. */
  owner: string | null;
  controlled: boolean;
  me: { id: string; role: string };
}): ClaimAction {
  if (o.claim) return { kind: 'release', blocked: o.claim.by === o.me.id || o.me.role === 'owner' || o.me.role === 'admin' ? null : `${o.claim.by} holds the claim. Only they or an admin can release it.` };
  const blocked = OPERATOR.includes(o.me.role) ? null : 'Claiming needs the operator role or above.';
  if (o.owner !== null) return o.owner ? { kind: 'none' } : { kind: 'claim', via: 'teams', blocked };
  if (o.origin === 'discovered' && !o.controlled) return { kind: 'claim', via: 'session', blocked };
  return { kind: 'none' };
}
